import type React from 'react'
import { useContext, useMemo, useState } from 'react'

import { Link } from '@tanstack/react-router'
import { groupBy, sortBy } from 'lodash-es'
import { Clock, Flag, Map as MapIcon, X } from 'lucide-react'

import { AdminHeader } from '@/components/organisms/AdminHeader/AdminHeader'
import { Map, type MapData } from '@/components/organisms/Map'
import { MapTeamPositionContext } from '@/components/organisms/Map/Markers/MapTeamPositionProvider'
import { formatCost } from '@/components/utils/formatCost'
import { useGoogleMapConfiguration } from '@/lib/useGoogleMapConfiguration.ts'
import { cn } from '@/lib/utils'
import { piloteIdToLetter } from '@/model/utils/PiloteIdToLetter.tsx'
import { AppPageMapContext } from '@/pages/dashboard/AppPageMapProvider'
import { teamStatus } from '@/pages/dashboard/data/teamStatus'
import { teamMapPositions } from '@/state/assignments/teamMapPositions'
import { type AssignmentTeam, type GlobalAssignment } from '@/state/assignments/types'

export interface GlobalAssignmentDetailsViewProps {
  assignment: GlobalAssignment
}

// Status colors based on transport request status - matching official dashboard colors
const getStatusColor = (team: AssignmentTeam): { fill: string; border?: string } => {
  if (!team.transportRequest?.status) {
    return { fill: 'bg-gray-400' } // No transport request assigned
  }
  switch (team.transportRequest.status) {
    case 'assigned':
      return { fill: 'bg-transparent', border: 'border-2 border-blue-600' } // Blue outline only
    case 'allocated':
      return { fill: 'bg-blue-600' } // Solid blue
    case 'on_board':
      return { fill: 'bg-green-500' } // Green
    default:
      return { fill: 'bg-gray-400' }
  }
}

// Get status indicator bar (black line above the color for manual transmission teams)
const getStatusIndicator = (team: AssignmentTeam): boolean =>
  // Show black line for teams with manual transmission vehicle
  team.vehicle?.manual ?? false

export const GlobalAssignmentDetailsView: React.FC<GlobalAssignmentDetailsViewProps> = ({ assignment }) => {
  const [isMapOpen, setIsMapOpen] = useState(false)

  const googleMapConfiguration = useGoogleMapConfiguration()
  const { onMapLoad, onMapUnmount } = useContext(AppPageMapContext)

  // Destructure assignment first before using in useMemo hooks
  const {
    _id: assignmentId,
    regions,
    previousId,
    nextId,
    costs,
    timestamp,
    solver,
    teams,
    transportRequests,
  } = assignment

  // Group teams by regionId (directly from team.regionId)
  const teamsByRegion = useMemo(() => {
    const grouped = groupBy(teams, (team) => team.regionId)
    // Sort teams within each region by cost
    return Object.fromEntries(
      Object.entries(grouped).map(([regionId, regionTeams]) => [
        regionId,
        sortBy(regionTeams, (team) => costs.details.teams[team._id]?.cost ?? 0),
      ])
    )
  }, [teams, costs.details.teams])

  // Group transport requests by regionId (directly from tr.regionId)
  const transportRequestsByRegion = useMemo(() => {
    const grouped = groupBy(transportRequests, (tr) => tr.regionId)
    // Sort transport requests within each region by cost
    return Object.fromEntries(
      Object.entries(grouped).map(([regionId, regionTRs]) => [
        regionId,
        sortBy(regionTRs, (tr) => costs.details.transportRequests[tr._id]?.cost ?? 0),
      ])
    )
  }, [transportRequests, costs.details.transportRequests])

  // Get region IDs that have teams (for team headers)
  const teamRegionIds = useMemo(
    () => Object.keys(teamsByRegion).filter((regionId) => (teamsByRegion[regionId]?.length ?? 0) > 0),
    [teamsByRegion]
  )

  // Get region IDs that have transport requests (for TR rows)
  const trRegionIds = useMemo(
    () =>
      Object.keys(transportRequestsByRegion).filter(
        (regionId) => (transportRequestsByRegion[regionId]?.length ?? 0) > 0
      ),
    [transportRequestsByRegion]
  )

  const teamPositions = useMemo(() => teamMapPositions(teams, costs), [teams, costs])

  // Get ranked costs for a transport request (sorted by cost, excluding infinite costs)
  const getRankedCosts = (transportRequestId: string): Record<string, number> => {
    const assignments = costs.details.assignments?.[transportRequestId]
    if (!assignments) return {}
    // Filter out entries with infinite or very large costs, then sort by cost
    const finiteEntries = Object.entries(assignments).filter(([, a]) => a.cost != null && Number.isFinite(a.cost))
    const sorted = finiteEntries.sort(([, a], [, b]) => a.cost - b.cost)
    return Object.fromEntries(sorted.map(([teamId], index) => [teamId, index + 1]))
  }

  // Get the first region's map configuration as default, or calculate center from all regions
  const mapConfig = useMemo(() => {
    if (regions.length === 0) {
      return { zoom: 8, center: { lat: 46.8139, lng: -71.208 } }
    }

    // If we have map configurations, use the first one as base
    const firstRegionWithMap = regions.find((r) => r.mapConfiguration)
    if (firstRegionWithMap?.mapConfiguration) {
      return {
        zoom: Math.max(8, (firstRegionWithMap.mapConfiguration.zoom ?? 11) - 2), // Zoom out for global view
        center: {
          lat: firstRegionWithMap.mapConfiguration.center?.latitude ?? 46.8139,
          lng: firstRegionWithMap.mapConfiguration.center?.longitude ?? -71.208,
        },
      }
    }

    return { zoom: 8, center: { lat: 46.8139, lng: -71.208 } }
  }, [regions])

  const mapData = useMemo(
    () => ({
      zoom: mapConfig.zoom,
      center: mapConfig.center,
      transportRequests: [
        ...transportRequests.map(({ _id, piloteId, origin, destination }) => ({
          id: _id,
          status: 'pending-origin' as const,
          locationLabel: piloteId != null ? piloteIdToLetter(piloteId) : '?',
          origin: {
            lat: origin.geolocation.latitude,
            lng: origin.geolocation.longitude,
          },
          destination: {
            lat: destination.geolocation.latitude,
            lng: destination.geolocation.longitude,
          },
        })),
        ...teams.flatMap<NonNullable<MapData['transportRequests']>[number]>((team) => {
          if (!team.currentTransportRequest || !team.transportRequest?.status) {
            return []
          }
          const origin = {
            lat: team.currentTransportRequest.origin.geolocation.latitude,
            lng: team.currentTransportRequest.origin.geolocation.longitude,
          }
          const destination = {
            lat: team.currentTransportRequest.destination.geolocation.latitude,
            lng: team.currentTransportRequest.destination.geolocation.longitude,
          }
          switch (team.transportRequest.status) {
            case 'assigned':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-assigned' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            case 'allocated':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-allocated' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            case 'on_board':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-on-board' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            default:
              return []
          }
        }),
      ],
      teams: teams.map(({ _id, status, number }) => ({
        id: _id,
        status: teamStatus(status) ?? ('available' as const),
        carNumber: String(number ?? ''),
      })),
    }),
    [mapConfig, teams, transportRequests]
  )

  return (
    <div className="flex h-screen flex-col">
      <AdminHeader title="Plan Global" />
      <div className="flex min-h-0 flex-1 bg-white">
        {/* Grid Section with Header */}
        <div className="flex flex-1 flex-col border-r border-gray-200">
          {/* Grid Content */}
          <div className="flex-1 overflow-auto p-4">
            {/* Navigation Links - offset by Solver column width (96px + 4px gap) */}
            <div className="flex items-center gap-8 pl-[100px] text-[12px] text-gray-500">
              {previousId ? (
                <Link
                  to="/assignments/$assignmentId"
                  params={{ assignmentId: previousId }}
                  search={{ region_id: undefined, limit: undefined }}
                  className="hover:text-gray-700">
                  {'< PREVIOUS'}
                </Link>
              ) : (
                <span className="text-gray-300">{'< PREVIOUS'}</span>
              )}
              {nextId ? (
                <Link
                  to="/assignments/$assignmentId"
                  params={{ assignmentId: nextId }}
                  search={{ region_id: undefined, limit: undefined }}
                  className="hover:text-gray-700">
                  {'NEXT >'}
                </Link>
              ) : (
                <span className="text-gray-300">{'NEXT >'}</span>
              )}
              <Link
                to="/assignments"
                search={{ region_id: undefined, limit: undefined }}
                className="hover:text-gray-700">
                LIST
              </Link>
              <a
                href={`/plans/${assignmentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-700">
                LEGACY
              </a>
            </div>
            {/* Date and Summary - offset by Solver column width */}
            <div className="mt-3 pl-[100px]">
              <div className="text-sm text-black">{timestamp.toLocaleString()}</div>
              <div className="mt-1 text-xs text-gray-500">
                {teams.length} équipes • {transportRequests.length} demandes
              </div>
            </div>
            {/* Region Headers Row - only show regions with teams */}
            <div className="mt-2 flex gap-1 overflow-x-auto">
              <div className="w-[96px] shrink-0" /> {/* Spacer for Solver column */}
              {teamRegionIds.map((regionId) => {
                const regionTeams = teamsByRegion[regionId] || []
                const region = regions.find((r) => r._id === regionId)
                const regionName = region?.name ?? ''
                // Calculate width: 60px per team + 4px gap between teams (gap-1 = 4px)
                const totalWidth = regionTeams.length * 60 + Math.max(0, regionTeams.length - 1) * 4
                return (
                  <div
                    key={regionId}
                    className="flex h-6 shrink-0 items-center justify-center overflow-hidden rounded-t-sm bg-blue-100 px-1"
                    style={{ width: `${totalWidth}px` }}
                    title={regionName}>
                    <span className="truncate text-[10px] font-semibold text-blue-800">{regionName}</span>
                  </div>
                )
              })}
            </div>
            <div className="flex gap-1 overflow-x-auto pb-1">
              <div className="w-[96px] shrink-0" /> {/* Spacer for Solver column */}
              {teamRegionIds.map((regionId) => {
                const regionTeams = teamsByRegion[regionId] || []
                return regionTeams.map((team) => {
                  const statusColors = getStatusColor(team)
                  return (
                    <div key={team._id} className="flex h-2 w-[60px] shrink-0 flex-col justify-center gap-px">
                      {getStatusIndicator(team) && <div className="h-0.5 w-full rounded-sm bg-black" />}
                      <div className={cn('h-full w-full rounded-sm', statusColors.fill, statusColors.border)} />
                    </div>
                  )
                })
              })}
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {/* Solver Cost Cell */}
              <div className="flex h-10 w-[96px] shrink-0 flex-col justify-center rounded-sm px-2 py-1">
                <div className="flex w-full items-center gap-1">
                  <span className="text-[10px] text-black">Solver</span>
                  <span className="ml-auto text-right text-sm text-black">{formatCost(solver?.cost ?? 0)}</span>
                </div>
              </div>
              {teamRegionIds.map((regionId) => {
                const regionTeams = teamsByRegion[regionId] || []
                const region = regions.find((r) => r._id === regionId)
                const regionName = region?.name ?? ''
                return regionTeams.map((team) => {
                  const teamCosts = costs.details.teams[team._id]
                  const isWaiting = teamCosts?.availability === 'now'
                  const displayValue = isWaiting
                    ? (teamCosts?.idleTimeInMinutes ?? 0)
                    : teamCosts?.availability === 'unknown'
                      ? '?'
                      : (teamCosts?.availability ?? 0)
                  return (
                    <div
                      key={team._id}
                      className="flex h-10 w-[60px] shrink-0 flex-col items-end justify-center rounded-sm bg-black px-1.5 py-1 text-white"
                      title={regionName}>
                      <div className="flex w-full items-center justify-between">
                        <span className="text-lg font-bold">{team.number}</span>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[8px]">{Math.round(Number(displayValue) || 0)}</span>
                          {isWaiting ? <Clock className="h-2.5 w-2.5" /> : <Flag className="h-2.5 w-2.5" />}
                        </div>
                      </div>
                    </div>
                  )
                })
              })}
            </div>
            <div className="flex flex-col gap-1 overflow-x-auto pt-2">
              {trRegionIds.map((regionId, regionIndex) => {
                const regionTRs = transportRequestsByRegion[regionId] || []
                const region = regions.find((r) => r._id === regionId)
                const regionName = region?.name ?? ''

                // Skip regions with no transport requests
                if (regionTRs.length === 0) return null

                return (
                  <div key={regionId} className="flex flex-col gap-1">
                    {/* Region separator with name - only show before new regions (not the first one) */}
                    {regionIndex > 0 && (
                      <div className="mt-2 flex items-center gap-2 border-t border-blue-200 pt-2">
                        <span className="text-[10px] font-semibold text-blue-700">{regionName}</span>
                        <div className="h-px flex-1 bg-blue-200" />
                      </div>
                    )}

                    {regionTRs.map((transportRequest) => {
                      const trCosts = costs.details.transportRequests[transportRequest._id]
                      const rankedCosts = getRankedCosts(transportRequest._id)

                      return (
                        <div key={transportRequest._id} className="flex gap-1">
                          {/* Transport Request Header */}
                          <div className="flex h-10 w-[96px] shrink-0 flex-col items-end justify-center rounded-sm bg-black px-2 py-1 text-white">
                            <div className="flex w-full items-center justify-between">
                              <span className="text-lg font-bold">
                                {transportRequest.piloteId != null ? piloteIdToLetter(transportRequest.piloteId) : '?'}
                              </span>
                              <div className="flex items-center gap-0.5">
                                <span className="text-[8px]">{trCosts?.waitTime ?? 0}</span>
                                <Clock className="h-2.5 w-2.5" />
                              </div>
                            </div>
                          </div>

                          {/* Assignment Cells for all teams across all regions (with teams) */}
                          {teamRegionIds.map((otherRegionId) => {
                            const otherRegionTeams = teamsByRegion[otherRegionId] || []
                            return otherRegionTeams.map((team) => {
                              const solverCosts = costs.details.assignments?.[transportRequest._id]?.[team._id]
                              const solverSelected = solver?.assignations?.[transportRequest._id] === team._id
                              const isInfiniteCost = !solverCosts || !Number.isFinite(solverCosts.cost)
                              const rank = isInfiniteCost ? undefined : rankedCosts[team._id]
                              const isSameRegion = regionId === otherRegionId

                              if (!solverCosts || isInfiniteCost) {
                                return (
                                  <div
                                    key={team._id}
                                    className={cn(
                                      'flex h-10 w-[60px] shrink-0 flex-col items-start justify-end rounded-sm border px-1 py-1',
                                      isSameRegion ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                                    )}>
                                    <span className="text-[12px] leading-[1.2] font-bold text-gray-400">∞</span>
                                  </div>
                                )
                              }

                              return (
                                <div
                                  key={team._id}
                                  className={cn(
                                    'flex h-10 w-[60px] shrink-0 flex-col items-start justify-end rounded-sm px-1 py-1',
                                    solverSelected
                                      ? 'bg-green-300'
                                      : isSameRegion
                                        ? 'border border-blue-200 bg-blue-50'
                                        : 'border border-gray-200'
                                  )}>
                                  <span
                                    className={cn(
                                      'text-[10px] leading-[1.2]',
                                      solverSelected ? 'text-black' : 'text-gray-500'
                                    )}>
                                    {rank}
                                  </span>
                                  <span
                                    className={cn(
                                      'text-[12px] leading-[1.2] font-bold',
                                      solverSelected ? 'text-black' : 'text-gray-500'
                                    )}>
                                    {formatCost(solverCosts.cost)}
                                  </span>
                                </div>
                              )
                            })
                          })}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Map Toggle Button - visible when map is closed */}
        {!isMapOpen && (
          <button
            onClick={() => {
              setIsMapOpen(true)
            }}
            className="flex w-10 shrink-0 flex-col items-center justify-center border-l border-gray-200 bg-gray-50 transition-colors hover:bg-gray-100">
            <MapIcon className="h-5 w-5 text-gray-600" />
          </button>
        )}

        {/* Map Section - Full height, takes remaining space */}
        <div
          className={cn(
            'flex min-h-0 flex-col overflow-hidden border-l border-gray-200 transition-all duration-300 ease-in-out',
            isMapOpen ? 'flex-1' : 'w-0 border-l-0'
          )}>
          {/* Map Header with Close Button */}
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">Carte</span>
            <button
              onClick={() => {
                setIsMapOpen(false)
              }}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Map */}
          <div className="min-h-0 flex-1">
            <MapTeamPositionContext.Provider value={teamPositions}>
              <Map
                className="h-full w-full"
                height="100%"
                data={mapData}
                activeView="all"
                onMapLoad={onMapLoad}
                onMapUnmount={onMapUnmount}
                googleMapConfiguration={googleMapConfiguration}
              />
            </MapTeamPositionContext.Provider>
          </div>
        </div>
      </div>
    </div>
  )
}
